import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Copy, Check, Mail } from 'lucide-react';
import { CONTACT_EMAIL, HUB_URL, MAILTO_PLAIN, mailtoHref, type InquiryKind } from '../lib/site';
import PageHeader from '../components/ui/PageHeader';
import Tabs from '../components/ui/Tabs';

type Path = Exclude<InquiryKind, 'general'>;

interface PathInfo {
  id: Path;
  label: string;
  chip: React.ReactNode;
  intro: string;
  include: string[];
  body: string[];
  side: { to: string; text: string };
  cta: string;
}

const PATHS: PathInfo[] = [
  {
    id: 'development',
    label: 'Development',
    chip: <span className="chip-dev">Development · one-time</span>,
    intro: 'A new site or application, or changes to one. No hosting purchase is required.',
    include: [
      'What you want built and who it is for',
      'Your estimate from the pricing calculator, if you ran it (tier and price range)',
      'Whether outside services (ordering, booking, checkout) are involved',
      'Whether you need a server, accounts, or stored records',
      'Target dates, if any',
    ],
    body: [
      'Development inquiry (informational estimate — not a contract)',
      '',
      'What I want built:',
      '',
      'Estimate from the pricing calculator (tier / range), if any:',
      '',
      'Outside services involved (ordering, booking, checkout …):',
      '',
      'Server, accounts, or stored records needed:',
      '',
      'Target dates:',
      '',
    ],
    side: { to: '/pricing?view=calculator', text: 'See development pricing first' },
    cta: 'Email a development inquiry',
  },
  {
    id: 'hosting',
    label: 'Hosting',
    chip: <span className="chip-host">Hosting · monthly</span>,
    intro: 'Managed hosting for a site you already have, or a build from someone else. No development work is required.',
    include: [
      'What you run (single page, multi-page, backend app, database app …)',
      'The plan the plan finder suggested, if you used it',
      'Whether you need a content-management interface',
      'Expected traffic and how sensitive the data is',
      'Where the site is hosted today, if it is moving',
    ],
    body: [
      'Hosting inquiry (informational estimate — not a contract)',
      '',
      'What I run:',
      '',
      'Suggested plan from the plan finder, if any:',
      '',
      'Content-management interface needed:',
      '',
      'Expected traffic / data sensitivity:',
      '',
      'Current hosting (if moving):',
      '',
    ],
    side: { to: '/plans', text: 'Find your plan first' },
    cta: 'Email a hosting inquiry',
  },
  {
    id: 'both',
    label: 'Both',
    chip: (
      <span className="inline-flex gap-1.5">
        <span className="chip-dev">Development · one-time</span>
        <span className="chip-host">Hosting · monthly</span>
      </span>
    ),
    intro: 'Build it and host it. The two are separate contracts and are itemized separately, so one-time and monthly costs never blur together.',
    include: [
      'Your development estimate (one-time)',
      'The optional hosting estimate from step 6 of the calculator (monthly)',
      'What you want built and any outside services involved',
      'Traffic expectations and data sensitivity',
    ],
    body: [
      'Development + hosting inquiry (informational estimates — not contracts)',
      '',
      'Development estimate (one-time):',
      '',
      'Hosting estimate (monthly):',
      '',
      'What I want built:',
      '',
      'Outside services involved:',
      '',
      'Traffic / data sensitivity:',
      '',
    ],
    side: { to: '/pricing?view=calculator', text: 'Run the calculator with optional hosting' },
    cta: 'Email a combined inquiry',
  },
];

const CopyEmail: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the mailto link and visible address still work */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="btn btn-quiet px-3"
      aria-label={copied ? 'Email address copied' : 'Copy email address'}
    >
      {copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
      <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
    </button>
  );
};

const Contact: React.FC = () => {
  const [params] = useSearchParams();
  const requested = params.get('path');
  const initial = (PATHS.find((p) => p.id === requested)?.id ?? 'development') as Path;

  return (
    <div className="page-inner pb-10">
      <PageHeader title="Contact" lead="One inbox, three starting points. Choose the one that fits so the conversation starts with the right questions." />

      <div className="panel p-4 mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <Mail className="w-5 h-5 text-primary-300 hidden sm:block" aria-hidden="true" />
        <p className="text-sm text-secondary-300 flex-1 min-w-0">
          Email{' '}
          <a href={MAILTO_PLAIN} className="font-semibold text-primary-300 hover:text-primary-200 underline underline-offset-4 break-all">
            {CONTACT_EMAIL}
          </a>
        </p>
        <CopyEmail />
      </div>

      <Tabs
        label="Inquiry type"
        defaultValue={initial}
        tabs={PATHS.map((p) => ({
          id: p.id,
          label: p.label,
          ariaLabel: `${p.label} inquiry`,
          panel: (
            <div className="panel p-4 sm:p-5">
              {p.chip}
              <p className="mt-3 text-sm text-secondary-200">{p.intro}</p>
              <h2 className="mt-4 text-sm font-semibold text-white">Helpful to include</h2>
              <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-secondary-300">
                {p.include.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <a href={mailtoHref(p.id, p.body)} className="btn btn-primary">
                  {p.cta}
                </a>
                <Link to={p.side.to} className="btn btn-outline">
                  {p.side.text}
                </Link>
              </div>
            </div>
          ),
        }))}
      />

      <section className="mt-6 space-y-3 text-sm text-secondary-300" aria-labelledby="after-you-write">
        <h2 id="after-you-write" className="text-lg font-bold text-white">After you write</h2>
        <p>
          You can request the full financial information at any time. Email{' '}
          <a href={MAILTO_PLAIN} className="font-semibold text-primary-300 hover:text-primary-200 underline underline-offset-4 break-all">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
        <p>
          For a serious inquiry, request a time to meet — online or in person — and discuss everything you need and want. Within 24 hours of that meeting, FairCorp emails you a comprehensive document of the assessment and the final pricing. You are welcome to amend what you want or need. FairCorp reserves up to 1 business day to re-review, then replies with an updated offer.
        </p>
        <p>
          FairCorp may also provide customized interfacing devices as needed, at additional cost. For example, a thrift store may want a kiosk that lists the items currently on hand for customers to view.
        </p>
      </section>

      <p className="mt-5 text-sm text-secondary-300">
        Existing customer with a problem?{' '}
        <a href={`${HUB_URL}/ticket`} className="text-primary-300 hover:text-primary-200 underline underline-offset-4">
          Open a ticket on the FairCorp Hub
        </a>
        . General questions are answered on the{' '}
        <Link to="/support" className="text-primary-300 hover:text-primary-200 underline underline-offset-4">
          Support page
        </Link>
        .
      </p>
    </div>
  );
};

export default Contact;
